import {
  LOCATION_TYPE_VALUE,
  RESOURCE_DOMAIN,
  RESOURCE_SEARCH_TYPE,
  SORT_BY,
  TRACKABLE_CARD_ACTIONS,
} from "@modules/resource-search/resource-search-helper.ts";
import { ContentSubpageParams } from "@plugins/content-subpage/composables/content-subpage.ts";
import type { RawSectionInfo } from "@modules/pending-action/service/section-display.utils.ts";
import { QuestionType } from "@/types/question.ts";
import { AssignmentMetadata } from "@/types/assignment.ts";
import { MediaType } from "@/plugins/media/media.constant";
import { RubricResourceModel } from "../grading-rubric/types";
import { QuestionModel } from "../editor/types";
import { SubjectLevel } from "@/common/subject-level-types.ts";
import { ResourceSearchCardAriaLabelConfig } from "@/common/card-types.ts";

export type Location = keyof typeof LOCATION_TYPE_VALUE;

export type ResourceDomain = keyof typeof RESOURCE_DOMAIN;

export type ResourceSearchType = keyof typeof RESOURCE_SEARCH_TYPE;

export type SortByType = (typeof SORT_BY)[number];

export type TrackableCardAction = (typeof TRACKABLE_CARD_ACTIONS)[number];

export type ListenerFunction = () => void;

export type ListenerMap = Record<string, ListenerFunction | undefined>;

export interface ResourceSearchProps extends RestrictSearchParams {
  keyword: string;
  resource: ResourceSearchType;
  filter?: SearchFilter;
  sortBy?: SearchSortBy;
  config: SearchConfig;
}

export const LessonSelectionTypeEnum = {
  gamebook: "gamebook",
  sectionOrLesson: "sectionOrLesson",
  activityOrSection: "activityOrSection",
  lesson: "lesson",
} as const;

export type LessonSelectionType =
  (typeof LessonSelectionTypeEnum)[keyof typeof LessonSelectionTypeEnum];

export const LessonSelectionModeEnum = {
  single: "single",
  multiple: "multiple",
} as const;

export type LessonSelectionMode =
  (typeof LessonSelectionModeEnum)[keyof typeof LessonSelectionModeEnum];

export type AddRubricFromLibraryEmit = (
  e: "rubricSelected",
  uuid: { data: { uuid: string } },
) => void;

export interface AddLessonFromLibraryContentSubpageProps
  extends ContentSubpageParams, RestrictSearchParams {
  selectionType: LessonSelectionType;
  selectionMode: LessonSelectionMode;
  extra: {
    isAttachingAttachment: boolean;
    appCategory: string; //INTEGRATOR_APP_FEATURE_CATEGORY;
    isCreatingPersonalTemplate: boolean;
    addFromEditorLocation: string; //FROM_EDITOR_PAGE_LOCATION
  };
}

export const LessonSelectionEventType = {
  gamebook: "gamebook",
  section: "section",
  activity: "activity",
  lesson: "lesson",
} as const;

export type AddLessonFromLibraryContentSubpageSelectionEvent =
  | {
      type: "gamebook";
      data: AddLessonFromLibraryContentSubpageGameSelectionData;
      onSuccess: (backCurrentSubpage: boolean) => void;
      onFail: (error: string) => void;
    }
  | {
      type: "section";
      data: AddLessonFromLibraryContentSubpageSectionSelectionData;
      onSuccess: (backCurrentSubpage: boolean) => void;
      onNext?: () => void;
      onFail: (error: string) => void;
    }
  | {
      type: "activity";
      data: AddLessonFromLibraryContentSubpageActivitySelectionData;
      onSuccess: (backCurrentSubpage: boolean) => void;
      onNext?: () => void;
      onFail: (error: string) => void;
    }
  | {
      type: "lesson";
      data: AddLessonFromLibraryContentSubpageLessonSelectionData;
      onSuccess: (backCurrentSubpage: boolean) => void;
      onFail: (error: string) => void;
    };

export interface AddMediaObjectFromLibraryContentSubpageSelectionEvent {
  data: MediaResultItem;
  onSuccess: () => void;
  onFailure: () => void;
}

export interface AddAppFromLibraryContentSubpageSelectionEvent {
  appUuid: string;
  onSuccess: () => void;
  onFailure: () => void;
}

export interface DomainSelectionObject {
  domainName: string;
  domainUuid: string;
  embeddedType: string;
}

export interface AddWebsiteFromLibraryContentSubpageSelectionEvent {
  domainSelection: DomainSelectionObject;
  onSuccess: () => void;
  onFailure: () => void;
}

export interface AddLessonFromLibraryContentSubpageGameSelectionData {
  gameBookId: number;
}

export interface AddLessonFromLibraryContentSubpageSectionSelectionData {
  sectionId: number[];
  lessonUuid: string;
  assignmentUuid?: string;
  isStudentGroupResource?: boolean;
  lessonTemplateUuid?: string;
  lessonId: number;
  title: string;
  status: string;
}

export interface AddLessonFromLibraryContentSubpageActivitySelectionData {
  activityId: number[];
  lessonUuid: string;
  lessonTemplateUuid?: string;
  lessonId: number;
  title: string;
  status: string;
}

export interface AddLessonFromLibraryContentSubpageLessonSelectionData {
  lessonUuid: string;
  assignmentUuid?: string;
  isStudentGroupResource?: boolean;
  lessonId: number;
  lessonType: "COURSE";
  title: string;
  status: string;
  duplicatable: boolean;
  printable: boolean;
  reattemptable: boolean;
}

export interface AddQuestionFromLibraryContentSubpageSelectionEventData {
  uuids: string[];
}

export interface AddQuestionFromLibraryContentSubpageSelectionEvent {
  data: AddQuestionFromLibraryContentSubpageSelectionEventData;
  onSuccess: () => void;
  onFailure: () => void;
}

export interface AddQuestionFromLibraryContentSubpageProps
  extends ContentSubpageParams, RestrictSearchParams {
  addFromMpqChildLayer: boolean;
  addFromMpqSubLayer: boolean;
}

export interface AddMediaObjectFromLibraryContentSubpageProps
  extends ContentSubpageParams, RestrictSearchParams {
  checkCopyRightForMOEResource: boolean;
  fromMoeLibrary: boolean;
}

export interface AddRubricsFromLibraryContentSubpageProps
  extends ContentSubpageParams, RestrictSearchParams {}

export interface AddAppFromLibraryContentSubpageProps
  extends ContentSubpageParams, RestrictSearchParams {
  fromResourceLibrary: boolean;
  gradable?: boolean;
}

export interface RestrictSearchParams {
  restrictQuestionType?: QuestionType[];
  restrictMediaType?: MediaType[];
  restrictLocation?: Location[];
}

export interface FlagRestrictParams {
  checkCopyRightForMOEResource?: boolean;
  fromMoeLibrary?: boolean;
  fromResourceLibrary?: boolean;
}

export interface ResourceSearchParams extends SearchFilterRequest, SearchSortBy {
  keyword: string;
  resource: ResourceSearchType;
  pageNo: number;
  pageSize: number;
}

export interface SearchFilter {
  learningOutcome?: string;
  parentTopic?: string;
  subject?: string;
  level?: string;
  location?: Location[];
  resourceType?: string; // QuestionType[] or MediaType[]
  authors?: string;
  syllabus?: string;
  topic?: string;
}

export interface SearchFilterRequest {
  subject?: string[];
  level?: string[];
  location?: Location[];
  resourceType?: QuestionType[] | MediaType[];
  authors?: string[];
  syllabus?: string;
  topic?: string[];
  parentTopic?: string[];
  learningOutcome?: string[];
  gradable?: boolean;
}

export interface SearchSortBy {
  sortBy?: SortByType;
  sortByOrder?: string;
}

export interface SearchConfig {
  bannerLabel?: "Find Resource" | "MOELibrary" | "CommunityGallery" | "Add From Library";
}

export interface SearchFilterCountRestrictionRequest {
  resourceOwnerTypes: Location[];
  questionTypes?: QuestionType[];
  mediaObjectTypes?: MediaType[];
}

export interface SearchFilterCountFilterRequest {
  levelAndStreamIds?: number[];
  subjectIds?: number[];
  resourceOwnerTypes?: Location[];
  questionTypes?: QuestionType[];
  mediaObjectTypes?: MediaType[];
}

export interface SearchFilterCountRequest {
  searchResourceType: ResourceSearchType;
  keyword: string;
  restriction: SearchFilterCountRestrictionRequest;
  filter?: SearchFilterCountFilterRequest;
  isFromMoeLibrary?: boolean;
  isCheckCopyRightForMOEResource?: boolean;
}

export interface SearchFilterOptionCountResponse {
  id?: number;
  name: string;
  count: number;
}

export interface SearchFilterCountResponse {
  levelAndStreams?: SearchFilterOptionCountResponse[];
  subjects?: SearchFilterOptionCountResponse[];
  resourceDomains?: SearchFilterOptionCountResponse[];
  questionTypes?: SearchFilterOptionCountResponse[];
  mediaObjectTypes?: SearchFilterOptionCountResponse[];
}

export interface SearchResult {
  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  length: number;
  start: number;
  error?: string | null;
  data:
    | LessonResultItem[]
    | QuestionResultItem[]
    | MediaResultItem[]
    | WebsiteResultItem[]
    | AppLibraryResultItem[]
    | RubricResultItem[];
}

export interface LessonResultItem {
  accessibleByAssociatedStudent: boolean;
  accessibleByTeacher: boolean;
  communityResourceUuid?: string;
  createdBy: string;
  createdDate: string;
  description: string;
  duplicatable: boolean;
  gameEnabled: boolean;
  highlights: LessonResultItemHighlights;
  lessonVoteCount: number;
  teacherReviewCount: number;
  viewCount: number;
  modifiedBy: string;
  modifiedByName: string;
  modifiedDate: string;
  moeLibraryUuid: string;
  ownerGroupName: string;
  publisher: string;
  resourceDomain: ResourceDomain;
  resourceTaggings: ResourceTaggings[];
  status: string;
  title: string;
  titleHighlighted: string;
  uuid: string;
  resourceUuid?: string;
  thumbnailUrl?: string;
  passcodeEnabled?: boolean;
  /**
   * Student self-study progress, merged client-side onto browse results (SLS-12277 AC#105).
   * Absent for teachers and for modules the student has not started.
   */
  pendingAction?: { status?: string; lastAttemptedDate?: string | null } | null;
  sectionInfo?: RawSectionInfo | null;
}

export interface QuestionResultItem {
  createdBy: string;
  createdDate: string;
  highlights: QuestionResultItemHighlights;
  modifiedDate: string;
  ownerGroupName: string;
  publisher: string;
  resourceDomain: ResourceDomain;
  resourceTaggings: ResourceTaggings[];
  title: string;
  type: string;
  uuid: string;
}

export interface QuestionSearchCardProps {
  labelConfig: {
    kind: string;
  };
  author: string;
  content: string;
  createdDate: string;
  highlights: QuestionResultItemHighlights;
  modifiedDate: string;
  resourceDomain: ResourceDomain;
  resourceType: string;
  subjectLevelList?: SubjectLevel[];
  questionTitle: string;
  type: ResourceSearchType;
  uuid: string;
  ariaLabelConfig: ResourceSearchCardAriaLabelConfig;
}

export interface MediaResultItem {
  availability: string;
  createdBy: string;
  createdDate: string;
  description?: string;
  downloadable: boolean;
  endDate?: string;
  fileDuration?: string;
  fileSize: string;
  fileStatus: string;
  fileUrl?: string;
  highlights: MediaResultItemHighlights;
  id: number;
  keywords?: string;
  licenseType: string;
  modifiedBy: string;
  modifiedDate: string;
  objectType: string;
  ownerGroup: string;
  ownerGroupId: number;
  resourceDomain: ResourceDomain;
  startDate?: string;
  status: string;
  thumbnailUrl?: string;
  title: string;
  uuid: string;
}

export interface MediaSearchCardProps {
  content: string;
  downloadable: boolean;
  labelConfig: {
    kind: string;
  };
  highlights: MediaResultItemHighlights;
  resourceDomain: ResourceDomain;
  resourceType: string;
  src: string;
  title: string;
  titleHighlighted: string;
  type: ResourceSearchType;
  uuid: string;
  ariaLabelConfig: ResourceSearchCardAriaLabelConfig;
}

export interface LessonResultItemHighlights {
  PRE_TAG: string;
  POST_TAG: string;
  title?: string;
  description?: string;
  notes?: string;
  keyword?: string;
  contentMap?: string;
  createdBy?: string;
  contributors: string[];
  subjectMap: Record<number, string>;
  levelAndStreamMap: Record<number, string>;
  sections: SectionActivityHighlight[];
  activities: SectionActivityHighlight[];
}

export interface QuestionResultItemHighlights {
  answer?: string;
  answerValue: string;
  contentMap?: string;
  createdBy?: string;
  defaultAnswer: string;
  keyword?: string;
  levelAndStreamMap: Record<number, string>;
  skillLevel?: string;
  subjectMap: Record<number, string>;
  title: string;
  stimulus?: string;
}

export interface MediaResultItemHighlights {
  description?: string;
  keyword?: string;
  title: string;
  transcript?: TranscriptHighlight;
}

export interface SectionActivityHighlight {
  id: number;
  pageNo: number;
  type: string;
  title: string;
  description?: string;
  notes?: string;
  contentMap?: string;
  parentPageNo?: number;
  subjectMap?: Record<number, string>;
  levelAndStreamMap?: Record<number, string>;
}

export interface TranscriptHighlight {
  lineNo?: number;
  startTime?: number;
  endTime?: number;
  transcriptText?: string;
}

export interface ResourceTaggings {
  allSchool: boolean;
  contentMap?: string;
  contentMapType?: string;
  id: number;
  levelAndStreamId: number;
  levelAndStreamName: string;
  schoolIds?: string;
  subjectId: number;
  subjectKey: string;
  subjectName: string;
}

export interface ResourceTypeOption {
  label: string;
  value: string;
  name: string;
}

export interface SubjectLevelPair {
  subjectKey: string | null;
  subjectName: string | null;
  levelAndStreamName: string | null;
  subjectId: number | null;
  levelAndStreamId: number | null;
}

export interface ModuleRouteParams {
  resourceDomain?: string;
  mode?: string;
  lessonUuid?: string;
  assignmentUuid?: string;
  sectionId?: string | null;
  admin?: string;
}

export interface SortByOption {
  name?: string;
  value?: string;
}

export interface WebsiteResultItemHighlight {
  name: string | null;
  description: string | null;
  keyword: string | null;
}

export interface WebsiteResultItem {
  id: number;
  uuid: string;
  name: string;
  description: string;
  homeUrl: string;
  iconUrl: string | null;
  modifiedDate: [number, number, number, number, number, number]; // Tuple representing [year, month, day, hour, minute, second] for.e.g. [2025, 12, 5, 10, 23, 1]
  modifiedDateUI: string;
  enabled: boolean;
  whitelistDomains: string[];
  highlights: WebsiteResultItemHighlight;
}

export interface AppLibraryResultItemHighlight {
  name: string | null;
  description: string | null;
  keyword: string | null;
}

export interface AppLibraryResultItem {
  uuid: string;
  name: string;
  description: string;
  division: string;
  type: string;
  modifiedByName: string;
  modifiedDate: [number, number, number, number, number, number]; // Tuple representing [year, month, day, hour, minute, second] for.e.g. [2025, 12, 5, 10, 23, 1]
  modifiedDateUI: string;
  appAvailabilityType: string;
  iconUrl: string;
  highlights: AppLibraryResultItemHighlight;
  enabled: boolean;
  embeddable: boolean;
  installable: boolean;
  launchable: boolean;
}

export interface SubjectAndLevel {
  subject: string[];
  levelAndStream: string[];
}

export interface RubricResultItem {
  uuid: string;
  title: string;
  notes: string | null;
  keywords: string | null;
  resourceDomain: ResourceDomain;
  createdBy: string;
  createdById: number;
  createdDate: string;
  modifiedBy: string;
  modifiedById: number;
  modifiedDate: string;
  rangeList: RubricRange[];
  criteriaList: RubricCriteria[];
  criteriaRangeList: RubricCriteriaRange[];
  resourceTaggings: ResourceTaggings[];
  highlights?: RubricResultItemHighlights;
  status: string;
}

export interface RubricRange {
  id: number;
  gradingRubricsId: number;
  description: string;
  indexNo: number;
}

export interface RubricCriteria {
  id: number;
  gradingRubricsId: number;
  description: string;
  indexNo: number;
}

export interface RubricCriteriaRange {
  gradingRubricId: number;
  gradingRubricCriteriaId: number;
  gradingRubricRangeId: number;
  criteriaIndex: number;
  rangeIndex: number;
  maxScore: number;
  description: string;
}

export interface RubricResultItemHighlights {
  title?: string;
  criterion?: string;
  keyword?: string | null;
  contentMap?: string;
  bandDescription?: string | null;
  notes?: string | null;
  subjectMap?: Record<string, string>;
  skillLevel?: string | null;
  levelAndStreamMap?: Record<string, string> | null;
}

export interface SearchResourceModuleData extends SearchResourceLesson {
  metaData: AssignmentMetadata;
  sequentialSectionCompletionReq: boolean;
  overallProgress: {
    numStudentTotal?: number;
    numStudentCompleted?: number;
  };
  moduleAttemptStatus: {
    status: string;
    lastAttemptedDateTime: string;
    completedDateTime: string;
  };
  sectionData: Record<string, never>;
}

export interface SearchModuleDetail extends ContentSubpageParams {
  selectionType?: LessonSelectionType;
  selectionMode?: LessonSelectionMode;
  title: string;
  ownerGroupId: number;
  hasSelectAction: boolean;
  isContextCreateAssignment: boolean;
  type: string;
  uuid: string;
  resourceDomain: ResourceDomain;
  backLabelFromParent: string;
  isAttachingAttachment: boolean;
  showHeader: boolean;
  isSelectingResource: boolean;
  location: string;
  lesson: SearchResourceLesson;
  isFromGamification: boolean;
  highlights: LessonResultItemHighlights;
  moduleData: SearchResourceModuleData | null;
  status: string;
  assignmentUuid: string;
  template: {
    uuid: string;
  };
}

export interface SearchResourceLesson {
  uuid: string;
  title: string;
  showSectionCover: boolean;
  passcodeEnabled: boolean;
  resourceDomain: ResourceDomain;
  moeLibraryUuid?: string;
  thumbnailUrl: string;
  description: string;
  gameEnabled: boolean;
  subjectAndLevelList: [];
  publisher: string;
  createdByName: string;
  ownerId: number;
  createdDate: string;
  modifiedDate: string;
  modifiedByName: string;
  modifiedById: number;
  viewCount: number;
  keywords: string;
  lessonActivityList: [];
  ownerGroup: {
    name: string;
  };
  duplicatable: boolean;
  printable: boolean;
  reattemptable: boolean;
  status: string;
  gameBookModel?: GameBookModel;
}

export interface GameBookModel {
  id: number;
  title: string;
  description: string;
  gameStoryBackgroundImage: string;
  gameStoryBackgroundImageUrl: string;
}

export interface SearchQuestionDetail extends QuestionModel {
  highlights: QuestionResultItemHighlights;
  gradingRubric?: RubricResourceModel;
}
